import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  UncontrolledTooltip,
} from 'reactstrap';
import CouponService from '../../service/coupon.service';
import { formatCentsToBRL, parseBRLToCents } from '../../utils/currency';
import { useNotification } from '../../contexts/NotificationContext';

const PREVIEW_SUBTOTAL = 5000;

const HelpLabel = ({ id, children, tip }) => (
  <Label className="d-flex align-items-center" style={{ gap: 6 }}>
    <span>{children}</span>
    <i
      id={id}
      className="tim-icons icon-alert-circle-exc text-muted"
      style={{ fontSize: 14, cursor: 'help' }}
    />
    <UncontrolledTooltip target={id} placement="top">
      {tip}
    </UncontrolledTooltip>
  </Label>
);

const apiMessage = (err, fallback) => {
  const msg = err?.response?.data?.message;
  if (Array.isArray(msg)) return msg[0] || fallback;
  return msg || fallback;
};

const toDateInput = (value) => {
  if (!value) return '';
  return String(value).slice(0, 10);
};

const CouponForm = ({ coupon, onClose, accountId, show, setShow }) => {
  const { notify } = useNotification();
  const isEdit = !!coupon;
  const [saving, setSaving] = useState(false);
  const [code, setCode] = useState('');
  const [type, setType] = useState('PERCENTAGE');
  const [percentValue, setPercentValue] = useState('10');
  const [fixedInput, setFixedInput] = useState(formatCentsToBRL(1000));
  const [minSubtotalInput, setMinSubtotalInput] = useState('');
  const [maxUses, setMaxUses] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [active, setActive] = useState(true);

  useEffect(() => {
    setCode(coupon?.code || '');
    setType(coupon?.type || 'PERCENTAGE');
    setPercentValue(coupon?.type === 'PERCENTAGE' ? String(coupon.value) : '10');
    setFixedInput(
      coupon?.type === 'FIXED'
        ? formatCentsToBRL(coupon.value)
        : formatCentsToBRL(1000),
    );
    setMinSubtotalInput(
      coupon?.minSubtotalCents ? formatCentsToBRL(coupon.minSubtotalCents) : '',
    );
    setMaxUses(coupon?.maxUses != null ? String(coupon.maxUses) : '');
    setExpiresAt(toDateInput(coupon?.expiresAt));
    setActive(coupon?.active ?? true);
  }, [coupon, show]);

  const previewDiscount = useMemo(() => {
    if (type === 'PERCENTAGE') {
      const pct = Number(percentValue);
      if (!pct || pct < 1) return 0;
      return Math.min(PREVIEW_SUBTOTAL, Math.floor((PREVIEW_SUBTOTAL * pct) / 100));
    }
    return Math.min(PREVIEW_SUBTOTAL, parseBRLToCents(fixedInput) || 0);
  }, [type, percentValue, fixedInput]);

  const handleClose = () => {
    if (setShow) setShow(false);
    if (onClose) onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const normalized = code.trim().toUpperCase().replace(/\s+/g, '');
    if (!normalized) {
      notify('tr', 'warning', 'Informe o código do cupom', 'icon-alert-circle-exc');
      return;
    }
    const value =
      type === 'PERCENTAGE' ? Number(percentValue) : parseBRLToCents(fixedInput);
    if (!value || value < 1) {
      notify('tr', 'warning', 'Informe um valor de desconto válido', 'icon-alert-circle-exc');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        code: normalized,
        type,
        value,
        maxUses: maxUses.trim() ? Number(maxUses) : null,
        expiresAt: expiresAt || null,
        minSubtotalCents: minSubtotalInput ? parseBRLToCents(minSubtotalInput) : 0,
        active,
      };
      if (isEdit) {
        await CouponService.update(coupon.id, payload, accountId);
        notify('tr', 'success', 'Cupom atualizado', 'icon-check-2');
      } else {
        await CouponService.create(payload, accountId);
        notify('tr', 'success', 'Cupom criado', 'icon-check-2');
      }
      handleClose();
    } catch (err) {
      notify('tr', 'danger', apiMessage(err, 'Não foi possível salvar o cupom'), 'icon-alert-circle-exc');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={!!show} toggle={handleClose} modalClassName="modal-form">
      <ModalHeader toggle={handleClose}>
        {isEdit ? 'Editar cupom' : 'Novo cupom'}
      </ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody style={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' }}>
          <FormGroup>
            <Label for="couponCode">Código</Label>
            <Input
              id="couponCode"
              value={code}
              placeholder="PROMO10"
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              required
              style={{ letterSpacing: 1, fontFamily: 'monospace', fontWeight: 700 }}
            />
          </FormGroup>

          <FormGroup>
            <HelpLabel
              id="coupon-type-tip"
              tip="O desconto vale só para os itens. A taxa de entrega não entra."
            >
              Tipo de desconto
            </HelpLabel>
            <div className="d-flex" style={{ gap: 8 }}>
              <Button
                type="button"
                color={type === 'PERCENTAGE' ? 'primary' : 'secondary'}
                outline={type !== 'PERCENTAGE'}
                className="flex-fill"
                onClick={() => setType('PERCENTAGE')}
              >
                Porcentagem
              </Button>
              <Button
                type="button"
                color={type === 'FIXED' ? 'primary' : 'secondary'}
                outline={type !== 'FIXED'}
                className="flex-fill"
                onClick={() => setType('FIXED')}
              >
                Valor fixo
              </Button>
            </div>
          </FormGroup>

          {type === 'PERCENTAGE' ? (
            <FormGroup>
              <Label for="couponPercent">Porcentagem</Label>
              <div className="position-relative">
                <Input
                  id="couponPercent"
                  type="text"
                  inputMode="numeric"
                  value={percentValue}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 3);
                    setPercentValue(digits);
                  }}
                  onBlur={() => {
                    const n = Math.min(100, Math.max(1, Number(percentValue) || 1));
                    setPercentValue(String(n));
                  }}
                  required
                  style={{ paddingRight: 36 }}
                />
                <span
                  className="text-muted"
                  style={{
                    position: 'absolute',
                    right: 14,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    fontWeight: 600,
                  }}
                >
                  %
                </span>
              </div>
            </FormGroup>
          ) : (
            <FormGroup>
              <Label for="couponFixed">Valor do desconto</Label>
              <Input
                id="couponFixed"
                inputMode="numeric"
                value={fixedInput}
                placeholder="R$ 0,00"
                onChange={(e) => setFixedInput(formatCentsToBRL(parseBRLToCents(e.target.value)))}
                required
              />
            </FormGroup>
          )}

          <FormGroup>
            <HelpLabel
              id="coupon-min-tip"
              tip="Opcional. Impede usar o cupom em pedidos muito pequenos."
            >
              Pedido mínimo
            </HelpLabel>
            <Input
              inputMode="numeric"
              value={minSubtotalInput}
              placeholder="Sem mínimo"
              onChange={(e) => {
                const cents = parseBRLToCents(e.target.value);
                setMinSubtotalInput(cents ? formatCentsToBRL(cents) : '');
              }}
            />
          </FormGroup>

          <FormGroup>
            <HelpLabel
              id="coupon-uses-tip"
              tip="Quantas vezes o código pode ser usado no total. Vazio = ilimitado."
            >
              Limite de usos
            </HelpLabel>
            <Input
              type="number"
              min={1}
              value={maxUses}
              placeholder="Ilimitado"
              onChange={(e) => setMaxUses(e.target.value)}
            />
          </FormGroup>

          <FormGroup>
            <Label for="couponExpires">Data de expiração</Label>
            <Input
              id="couponExpires"
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
          </FormGroup>

          <FormGroup check className="mb-3">
            <Label check>
              <Input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
              />
              <span className="form-check-sign">
                <span className="check" />
              </span>{' '}
              Ativo
            </Label>
          </FormGroup>

          <div
            className="p-3 rounded"
            style={{ background: 'rgba(29,140,248,0.08)', border: '1px solid rgba(29,140,248,0.2)' }}
          >
            <div className="text-muted" style={{ fontSize: 12, marginBottom: 4 }}>
              Prévia
            </div>
            <div style={{ fontSize: 14 }}>
              Em um pedido de {formatCentsToBRL(PREVIEW_SUBTOTAL)}, o desconto será{' '}
              <strong>{formatCentsToBRL(previewDiscount)}</strong>.
            </div>
          </div>
        </ModalBody>
        <ModalFooter className="pt-3">
          <div className="w-100 d-flex justify-content-end">
            <Button color="secondary" onClick={handleClose} className="mr-2" disabled={saving}>
              Cancelar
            </Button>
            <Button color="primary" type="submit" disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default CouponForm;
